import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import axios from 'axios'
import { addDays, differenceInCalendarDays, isBefore } from 'date-fns'

import { FormProvider, RHFDateField } from '../../common/hook-form'
import { apiConstants } from '../../common/constants'
import { formatDate } from '../../common/util'
import PageTitle from '../../components/PageTitle'

const flatCardSx = {
  borderRadius: 2.5,
  border: '1px solid #e8e8e8',
  boxShadow: 'none',
  overflow: 'hidden', // so the top progress bar clips to the card's rounded corners
}

const MAX_RANGE_DAYS = 31

const Schema = Yup.object().shape({
  fromDate: Yup.mixed().required('From date is required'),
  toDate: Yup.mixed().required('To date is required'),
})

export default function RazorpayMonthlyDump() {
  const { enqueueSnackbar } = useSnackbar()
  const [isDownloading, setIsDownloading] = useState(false)

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: { fromDate: null, toDate: null },
  })
  const { handleSubmit, reset, watch } = methods

  const fromDate = watch('fromDate')
  const today = new Date()

  // Compute the To-Date picker's bounds based on the picked From-Date.
  // Effect: all dates outside [fromDate, fromDate + 30 days] are visually
  // disabled (greyed out) in the picker — user can't pick them at all.
  const fromDateObj = fromDate ? new Date(fromDate) : null
  const fromDateValid = fromDateObj && !Number.isNaN(fromDateObj.getTime())
  const toDateMin = fromDateValid ? fromDateObj : undefined
  const toDateMax = fromDateValid
    ? (isBefore(addDays(fromDateObj, MAX_RANGE_DAYS - 1), today)
        ? addDays(fromDateObj, MAX_RANGE_DAYS - 1)
        : today)
    : today

  const onSubmit = async (data) => {
    const startDate = formatDate(data.fromDate, { format: 'yyyy-MM-dd' })
    const endDate = formatDate(data.toDate, { format: 'yyyy-MM-dd' })

    // Defence in depth — picker already prevents this, but if anyone bypasses
    // the UI we still bail out before firing the request.
    const rangeDays = differenceInCalendarDays(new Date(data.toDate), new Date(data.fromDate)) + 1
    if (rangeDays > MAX_RANGE_DAYS) {
      enqueueSnackbar(`Date range cannot exceed ${MAX_RANGE_DAYS} days`, { variant: 'error' })
      return
    }
    if (rangeDays < 1) {
      enqueueSnackbar('To date must be on or after From date', { variant: 'error' })
      return
    }

    setIsDownloading(true)
    try {
      const res = await axios.get(
        `${apiConstants.BASE_LOCAL}${apiConstants.RAZORPAY_MONTHLY_DUMP}`,
        {
          params: { startDate, endDate },
          responseType: 'blob',
        }
      )

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `razorpay-pg-${startDate}-to-${endDate}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      enqueueSnackbar('CSV downloaded', { variant: 'success' })
    } catch (err) {
      let msg = 'Failed to download Razorpay dump'
      try {
        const text = await err?.response?.data?.text?.()
        const parsed = text && JSON.parse(text)
        msg = parsed?.errorMsg || parsed?.exception?.message || msg
      } catch (_) {
        msg = err?.message || msg
      }
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const onReset = () => {
    if (isDownloading) return
    reset({ fromDate: null, toDate: null })
  }

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      <PageTitle>Razorpay Monthly Dump</PageTitle>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a' }}>
            Razorpay Monthly Dump
          </Typography>
          <Typography sx={{ color: '#6b6b6b', fontSize: '0.78rem', mt: 0.3 }}>
            Download Razorpay PG transactions for a chosen date range (max {MAX_RANGE_DAYS} days) as a CSV.
          </Typography>
        </Box>
      </Stack>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={flatCardSx}>
          {/* Top progress bar — clearly visible whenever a request is in flight */}
          {isDownloading && (
            <LinearProgress
              sx={{
                height: 3,
                '& .MuiLinearProgress-bar': { transition: 'transform 0.2s linear' },
              }}
            />
          )}

          <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ mb: 2 }}
            >
              <Stack direction='row' alignItems='center' spacing={1}>
                <Box sx={{ display: 'flex', color: '#1565c0' }}>
                  <FilterAltOutlinedIcon fontSize='small' />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
                  Select Date Range
                </Typography>
              </Stack>
              {isDownloading && (
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: '#1565c0',
                    bgcolor: '#e8f0fd',
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 1.5,
                  }}
                >
                  Generating CSV…
                </Typography>
              )}
            </Stack>

            <Grid container spacing={1.5} alignItems='flex-start'>
              <Grid item xs={12} sm={6} md={3}>
                <RHFDateField
                  name='fromDate'
                  label='From Date'
                  maxDate={today}
                  disabled={isDownloading}
                  size='small'
                  fullWidth
                  InputLabelProps={{ style: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <RHFDateField
                  name='toDate'
                  label='To Date'
                  minDate={toDateMin}
                  maxDate={toDateMax}
                  disabled={!fromDateValid || isDownloading}
                  size='small'
                  fullWidth
                  InputLabelProps={{ style: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction='row' spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                  <LoadingButton
                    type='submit'
                    variant='contained'
                    size='medium'
                    startIcon={<DownloadIcon />}
                    loading={isDownloading}
                    loadingPosition='start'
                    disabled={!fromDateValid}
                    sx={{ textTransform: 'none', fontWeight: 600, minWidth: 170 }}
                  >
                    {isDownloading ? 'Downloading…' : 'Download CSV'}
                  </LoadingButton>
                  <Button
                    type='button'
                    variant='outlined'
                    size='medium'
                    startIcon={<RestartAltIcon />}
                    onClick={onReset}
                    disabled={isDownloading}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Typography sx={{ mt: 1.5, color: '#9e9e9e', fontSize: '0.7rem' }}>
              {isDownloading
                ? 'Fetching transactions and writing the CSV — please don\'t close this tab.'
                : `Pick a From Date first — the To Date picker will then allow only the next ${MAX_RANGE_DAYS} days. CSV is sorted by time, end date inclusive.`}
            </Typography>
          </CardContent>
        </Card>
      </FormProvider>
    </Box>
  )
}
